import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useDownloadDocument, useOpenDocument } from '@/hooks/api/useDocuments';
import { useGetJobStatus } from '@/hooks/api/useInternalJobs';
import { useAgreementByPayment } from '@/hooks/api/usePayments';
import { subscriptionKeys } from '@/hooks/api/useSubscriptions';
import { Paths } from '@/routes/paths';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ArrowRight, Download, FileCheck, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader';

interface Props {
  jobId: string;
  paymentId: string;
}

export const PaymentInvoice = ({ jobId, paymentId }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: agreement } = useAgreementByPayment(paymentId);
  const contractId = agreement?.id;
  // console.log(agreement)

  const { data: jobStatus, isLoading, error, refetch } = useGetJobStatus(jobId);

  const isProgress = jobStatus?.status === 'in_progress' || jobStatus?.status === 'pending';
  const completed = jobStatus?.status === 'completed';
  const success = jobStatus?.result?.success === true;

  const invoices: { invoiceId: string; documentId: string; fileName: string; error?: string }[] = jobStatus?.result?.invoices || [];
  if (jobStatus?.result?.documentId && invoices.length === 0) {
    invoices.push({
      invoiceId: jobStatus.result.invoiceId,
      documentId: jobStatus.result.documentId,
      fileName: jobStatus.result.fileName,
      error: jobStatus.result.error
    });
  }

  const combinedPdf = jobStatus?.result?.combinedPdf;
  const processingError = jobStatus?.error;
  const hasInternalErrors = invoices.some(inv => !!inv.error);

  useEffect(() => {
    if (!jobId || completed || processingError) return;
    const interval = setInterval(() => {
      refetch();
    }, 1000);
    return () => clearInterval(interval);
  }, [jobId, completed, processingError, refetch]);

  const { mutate: downloadDocument } = useDownloadDocument()
  const { mutate: openDocument } = useOpenDocument()

  const handleDownloadDocument = (documentId: string, fileName?: string) => {
    if (!documentId) return
    downloadDocument({
      documentId,
      fileName: fileName || 'documento.pdf',
    })
  }

  const handleOpenDocument = (documentId: string, fileName?: string) => {
    if (!documentId) return
    openDocument({
      documentId,
      fileName: fileName || 'documento.pdf',
    })
  }

  const handleDownloadCombined = () => {
    if (!combinedPdf) return;
    const linkSource = `data:application/pdf;base64,${combinedPdf}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = `comprobantes_combinados_${new Date().toLocaleDateString('es-AR')}.pdf`;
    downloadLink.click();
  }

  const handleForward = () => {
    if (contractId) {
      queryClient.invalidateQueries({ queryKey: ['agreement-payments', contractId] });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.subscriptions(contractId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.installments(contractId) });
      navigate({
        to: `${Paths.Management.Contracts}/${contractId}`,
      })
    } else {
      navigate({
        to: `${Paths.Management.Contracts}`,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error || !jobStatus) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error?.message || "Factura no encontrada"}</p>
        <Button variant="outline" onClick={() => navigate({ to: `${Paths.Management.Payments}` })}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
      </div>
    );
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-2xl font-bold">Facturación del pago realizado</h1>
      </NavHeader>

      <Card className="mt-6">
        {completed && success && !hasInternalErrors && (
          <CardHeader>
            <h1 className="text-2xl font-bold">{invoices.length > 1 ? 'Comprobantes generados' : 'Comprobante generado'}</h1>
          </CardHeader>
        )}
        {completed && success && hasInternalErrors && (
          <CardHeader>
            <h1 className="text-2xl font-bold">Recibo generado</h1>
          </CardHeader>
        )}
        {isProgress && (
          <CardHeader>
            <Alert variant={"default"}>
              <AlertTitle className="flex items-center mb-3">
                <AlertCircle className="mr-2 h-4 w-4" />Por favor, espere
              </AlertTitle>
              <AlertDescription>
                <i>Generando factura{invoices.length > 1 ? 's' : ''}...</i>
              </AlertDescription>
            </Alert>
          </CardHeader>
        )}
        <CardContent>
          <div className='flex flex-col items-start gap-10'>
            {((completed && !success) || processingError) && (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Error al generar factura, contacte al administrador. Adjuntar código de referencia: {jobId}</p>
              </div>
            )}
            {completed && hasInternalErrors && (
              <Alert variant={"warning"}>
                <AlertTitle className="flex items-center mb-3">
                  <AlertCircle className="mr-2 h-4 w-4" />Atención
                </AlertTitle>
                <AlertDescription>
                  <p>El servicio de AFIP no se encuentra disponible. Se generará un recibo temporal.</p>
                </AlertDescription>
              </Alert>
            )}

            {completed && success && invoices.length > 0 && (
              <div className='w-full flex flex-col gap-4'>
                {invoices.map((inv, index) => (
                  <div key={index} className='flex items-center justify-between p-4 border rounded'>
                    <div>
                      <p className='font-medium'>{inv.error ? 'Recibo' : 'Factura'}</p>
                      <p className='text-sm text-gray-500'>{inv.fileName}</p>
                    </div>
                    <div className='flex gap-4'>
                      <Button variant={"secondary"} onClick={() => handleOpenDocument(inv.documentId, inv.fileName)}>
                        <FileCheck className="h-4 w-4 mr-2" />
                        Ver
                      </Button>
                      <Button variant={"secondary"} onClick={() => handleDownloadDocument(inv.documentId, inv.fileName)}>
                        <Download className="h-4 w-4 mr-2" />
                        Descargar
                      </Button>
                    </div>
                  </div>
                ))}

                {combinedPdf && (
                  <div className='flex justify-center gap-5 mt-6'>
                    <Button variant={"info"} onClick={() => handleDownloadCombined()}>
                      <Download className="h-4 w-4 mr-2" />
                      Descargar PDF con múltiples comprobantes
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <div className='flex justify-center mt-10'>
        <Button variant={"success"} onClick={() => handleForward()}>
          Ver contratación
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </PageContainer>
  );
}
